/**
 * Validação de senha alinhada à política real do Cognito (pools de staging/prod):
 *   - mínimo 8 caracteres
 *   - ao menos uma letra minúscula
 *   - ao menos uma letra maiúscula
 *   - ao menos um número
 *   - ao menos um símbolo (caractere especial)
 *
 * Fonte da verdade: describe-user-pool dos pools importados. Se a política do
 * Cognito mudar, ajuste POLICY aqui para manter frontend e backend coerentes.
 */

export const PASSWORD_POLICY = {
  minLength: 8,
  requireLowercase: true,
  requireUppercase: true,
  requireNumbers: true,
  requireSymbols: true,
};

/**
 * Valida uma senha contra a política. Retorna a lista de requisitos NÃO atendidos
 * (vazia = senha válida). Cada item tem { key, label } para exibição no idioma.
 * @param {string} password
 * @returns {{key: string, label: string}[]} requisitos faltantes
 */
export function getPasswordIssues(password) {
  const pw = password || '';
  const issues = [];
  if (pw.length < PASSWORD_POLICY.minLength) {
    issues.push({ key: 'minLength', label: `Mínimo ${PASSWORD_POLICY.minLength} caracteres` });
  }
  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(pw)) {
    issues.push({ key: 'lowercase', label: 'Uma letra minúscula' });
  }
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(pw)) {
    issues.push({ key: 'uppercase', label: 'Uma letra maiúscula' });
  }
  if (PASSWORD_POLICY.requireNumbers && !/[0-9]/.test(pw)) {
    issues.push({ key: 'number', label: 'Um número' });
  }
  // Símbolo: qualquer coisa que não seja letra/número/espaço.
  if (PASSWORD_POLICY.requireSymbols && !/[^A-Za-z0-9\s]/.test(pw)) {
    issues.push({ key: 'symbol', label: 'Um caractere especial (ex.: ! @ # $)' });
  }
  return issues;
}

/** @returns {boolean} true se a senha atende a toda a política. */
export function isPasswordValid(password) {
  return getPasswordIssues(password).length === 0;
}

/**
 * Mensagem única e legível com o que falta na senha (para toasts/erros de campo).
 * @param {string} password
 * @returns {string|null} null se válida; senão texto agregando os requisitos.
 */
export function getPasswordError(password) {
  const issues = getPasswordIssues(password);
  if (issues.length === 0) return null;
  return `A senha precisa de: ${issues.map(i => i.label.toLowerCase()).join(', ')}.`;
}
